import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from './useDebounce';

interface IUseApiSearchOptions {
  minLength?: number;
  debounceMs?: number;
  enabled?: boolean;
}

interface IUseApiSearchResult<T> {
  options: T[];
  loading: boolean;
  error: Error | null;
  searchText: string;
  setSearchText: (text: string) => void;
}

/**
 * Hook for integrating API search with components like MultiSearchDropdown
 *
 * @param searchFn - Async function that takes search term and optional AbortSignal, returns array of options
 * @param propOptions - Configuration options
 * @param options.minLength - Minimum search term length before triggering API call (default: 3)
 * @param options.debounceMs - Debounce delay in milliseconds (default: 1000)
 * @param options.enabled - Whether the search is enabled (default: true)
 *
 * @example
 * ```typescript
 * const { options, loading, error, searchText, setSearchText } = useApiSearch(
 *   async (term, signal) => {
 *     const response = await backendApi.post('/search',
 *       { filter: { term } },
 *       { signal }
 *     );
 *     return response.data.map(item => ({ value: item.id, label: item.name }));
 *   },
 *   { minLength: 3, debounceMs: 1000 }
 * );
 *
 * <MultiSearchDropdown
 *   text={searchText}
 *   options={options}
 *   loading={loading}
 *   errorMessage={error?.message}
 *   onTextChange={setSearchText}
 * />
 * ```
 */
export const useApiSearch = <T = any>(
  searchFn: (searchTerm: string, signal?: AbortSignal) => Promise<T[]>,
  propOptions: IUseApiSearchOptions = {}
): IUseApiSearchResult<T> => {
  const { minLength = 3, debounceMs = 1000, enabled = true } = propOptions;

  const [searchText, setSearchText, debouncedSearchText] = useDebounce('', debounceMs);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Keep track of the abort controller for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Store searchFn in a ref to avoid re-creating performSearch on every render
  // This prevents infinite loops when searchFn is an inline function that changes on every render
  const searchFnRef = useRef(searchFn);

  // Update the ref when searchFn changes (doesn't trigger re-renders)
  useEffect(() => {
    searchFnRef.current = searchFn;
  }, [searchFn]);

  const performSearch = useCallback(
    async (term: string) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Don't search if term is too short
      if (term.length < minLength) {
        setResults([]);
        setLoading(false);
        setError(null);
        return;
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);
      setError(null);

      try {
        // Use the ref to get the latest searchFn without causing re-renders
        const results = await searchFnRef.current(term, abortController.signal);

        // Only update state if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setResults(results);
          setLoading(false);
        }
      } catch (err) {
        // Don't treat abort as an error
        if (!abortController.signal.aborted) {
          const error = err instanceof Error ? err : new Error('Search failed');
          setError(error);
          setResults([]);
          setLoading(false);
        }
      }
    },
    [minLength]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    performSearch(debouncedSearchText);

    // Cleanup: abort any in-flight requests when component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedSearchText, enabled, performSearch]);

  return {
    options: results,
    loading,
    error,
    searchText,
    setSearchText,
  };
};
